import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { useUserContext } from '@/shared/contexts/userContext'
import { useFeatureFlagsWithWorkspace } from '@/shared/hooks/useFeatureFlags'
import { Playbook, PlaybookUserAction } from '@/types/playbook'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import HistoryOutlined from '@mui/icons-material/HistoryOutlined'
import LinkOutlined from '@mui/icons-material/LinkOutlined'
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined'
import React from 'react'

type PlaybookActionMenuProps = {
  onPlaybookAction: (item: PlaybookUserAction) => void
  playbook: Playbook
}

export const PlaybookActionMenu: React.FC<PlaybookActionMenuProps> = ({
  onPlaybookAction,
  playbook,
}) => {
  const { flags: [
    isDuplicationEnabled,
    isVersionControlEnabled,
    isDeleteEnabled,
    isPlaybookSharingEnabled,
  ] } = useFeatureFlagsWithWorkspace([
    'playbookDuplication',
    'playbookVersionControl',
    'playbookDeletion',
    'playbookSharing',
  ])
  const { user } = useUserContext()
  const isOwner = playbook?.created_by === user?._id

  const availableOptions = [
    isDuplicationEnabled,
    isVersionControlEnabled,
    isDeleteEnabled,
    isPlaybookSharingEnabled,
  ].filter(Boolean)
  const isSomeOptionEnabled = availableOptions.length > 0

  return isSomeOptionEnabled ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="cursor-pointer bg-grey-03/10"
        >
          {isSomeOptionEnabled && <MoreHorizOutlined sx={{ fontSize: 16 }} />}
        </Button>
      </DropdownMenuTrigger>
      {isSomeOptionEnabled && (
        <DropdownMenuContent align="end">
          {isPlaybookSharingEnabled && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => onPlaybookAction('copyLink')}
            >
              <LinkOutlined sx={{ fontSize: 16 }} className="mr-2" />
              Copy playbook link
            </DropdownMenuItem>
          )}
          {isDuplicationEnabled && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => onPlaybookAction('duplicate')}
            >
              <ContentCopyOutlined sx={{ fontSize: 16 }} className="mr-2" />
              Duplicate playbook
            </DropdownMenuItem>
          )}
          {isVersionControlEnabled && (
            <>
              {(isPlaybookSharingEnabled || isDuplicationEnabled) && (
                <DropdownMenuSeparator />
              )}
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => onPlaybookAction('versionHistory')}
              >
                <HistoryOutlined sx={{ fontSize: 16 }} className="mr-2" />
                Version history
              </DropdownMenuItem>
            </>
          )}
          {isDeleteEnabled && (
            <>
              {availableOptions.length > 1 && <DropdownMenuSeparator />}
              <DropdownMenuItem
                disabled={!isOwner}
                className="cursor-pointer"
                onClick={() => onPlaybookAction('delete')}
              >
                <DeleteOutlined sx={{ fontSize: 16 }} className="mr-2" />
                Delete playbook
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  ) : null
}
